import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MapSchemaEditor from '../components/map-editor/MapSchemaEditor';
import FieldInfoButton from '../components/field/FieldInfoButton';
import { mapEditorInfo } from '../components/map-editor/mapEditorInfo';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirmModal } from '../hooks/useConfirmModal';
import {
  SchemaElement,
  NodeWithPosition,
  EditorMode,
  MapData,
  AUTO_SAVE_INTERVAL,
} from '../types/mapEditor';
import {
  getMapData,
  getMapNodes,
  updateSchemaElements,
  updateNodePosition,
  uploadExportedImage,
  dataURLtoBlob,
} from '../services/mapEditorService';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0a0a15;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #1a1a2e;
  border-bottom: 1px solid #333;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2a2a3e;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MapTitle = styled.h1`
  color: #FFFFFF;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const ModeTabs = styled.div`
  display: flex;
  gap: 4px;
`;

const ModeTab = styled.button<{ $active: boolean }>`
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 600;
  background: ${props => props.$active ? '#4CAF50' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#4CAF50' : '#444'};
  border-radius: 4px;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#45a049' : '#2a2a3e'};
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 24px;
  background: #0f0f1a;
  border-top: 1px solid #333;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
`;

const StatusItem = styled.span<{ $type?: 'success' | 'error' | 'warning' }>`
  color: ${props => {
    switch (props.$type) {
      case 'success': return '#4CAF50';
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      default: return '#FFFFFF';
    }
  }};
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

const ErrorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #f44336;
  gap: 16px;
`;

const MapEditorPage: React.FC = () => {
  const { mapId } = useParams<{ mapId: string }>();
  const navigate = useNavigate();
  const { notifications, showSuccess, showError, removeNotification } = useNotification();
  const { confirm, modalProps } = useConfirmModal();

  const [mapData, setMapData] = useState<MapData | null>(null);
  const [nodes, setNodes] = useState<NodeWithPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<EditorMode>('schema');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load map data
  useEffect(() => {
    const loadData = async () => {
      if (!mapId) return;

      try {
        setLoading(true);
        setError(null);

        const [map, mapNodes] = await Promise.all([
          getMapData(mapId),
          getMapNodes(mapId),
        ]);

        setMapData(map);
        setNodes(mapNodes);
      } catch (err: any) {
        console.error('Failed to load map data:', err);
        setError(err.message || 'Failed to load map data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [mapId]);

  // Auto-save
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const timer = setTimeout(() => {
      // Trigger auto-save
      if (mapData) {
        handleSave(mapData.schemaElements || [], mapData.canvasBgColor || 'dark');
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearTimeout(timer);
  }, [hasUnsavedChanges, mapData]);

  // Warn on page leave if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = useCallback(async (elements: SchemaElement[], canvasBgColor: 'dark' | 'white', imageDataUrl?: string) => {
    if (!mapId) return;

    try {
      setSaving(true);

      // 1. Save schema elements
      const updatedMap = await updateSchemaElements(mapId, elements, canvasBgColor);

      // 2. Upload canvas as PNG and update exportedImageUrl (only if imageDataUrl provided)
      if (imageDataUrl) {
        const blob = dataURLtoBlob(imageDataUrl);
        const imageUrl = await uploadExportedImage(mapId, blob);
        setMapData(prev => prev ? { ...prev, ...updatedMap, exportedImageUrl: imageUrl } : null);
      } else {
        setMapData(updatedMap);
      }

      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      showSuccess('Saved', 'Map saved successfully', 3000);
    } catch (err: any) {
      console.error('Failed to save:', err);
      setError(err.message || 'Failed to save');
      showError('Error', err.message || 'Failed to save map', 5000);
    } finally {
      setSaving(false);
    }
  }, [mapId, showSuccess, showError]);

  const handleNodePositionUpdate = useCallback(async (
    nodeId: string,
    x: number,
    y: number,
    scale: number,
    locked: boolean
  ) => {
    if (!mapId) return;

    try {
      const updatedNode = await updateNodePosition(mapId, nodeId, x, y, scale, locked);
      setNodes(prev => prev.map(n => n.id === nodeId ? updatedNode : n));
    } catch (err: any) {
      console.error('Failed to update node position:', err);
    }
  }, [mapId]);

  const handleExportImage = useCallback(async (dataUrl: string) => {
    if (!mapId) return;

    try {
      setSaving(true);
      const blob = dataURLtoBlob(dataUrl);
      const imageUrl = await uploadExportedImage(mapId, blob);
      setMapData(prev => prev ? { ...prev, exportedImageUrl: imageUrl } : null);
      alert('Map exported successfully!');
    } catch (err: any) {
      console.error('Failed to export image:', err);
      alert('Failed to export map image');
    } finally {
      setSaving(false);
    }
  }, [mapId]);

  const handleBack = async () => {
    if (hasUnsavedChanges) {
      const confirmed = await confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        variant: 'warning',
      });
      if (confirmed) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          Loading map editor...
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !mapData) {
    return (
      <PageContainer>
        <ErrorContainer>
          <span>{error || 'Map not found'}</span>
          <BackButton onClick={() => navigate(-1)}>
            Go Back
          </BackButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
      <Header>
        <BackButton onClick={handleBack}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          Back
        </BackButton>

        <TitleRow>
          <MapTitle>{mapData.name} - Map Editor</MapTitle>
          <FieldInfoButton title="Map Editor Help" sections={mapEditorInfo} />
        </TitleRow>

        <ModeTabs>
          <ModeTab
            $active={mode === 'schema'}
            onClick={() => setMode('schema')}
          >
            Schema
          </ModeTab>
          <ModeTab
            $active={mode === 'nodes'}
            onClick={() => setMode('nodes')}
          >
            Nodes
          </ModeTab>
          <ModeTab
            $active={mode === 'devices'}
            onClick={() => setMode('devices')}
          >
            Mission Templates
          </ModeTab>
        </ModeTabs>
      </Header>

      <MapSchemaEditor
        mapId={mapId!}
        mapData={mapData}
        nodes={nodes}
        mode={mode}
        onSave={handleSave}
        onNodePositionUpdate={handleNodePositionUpdate}
        onExportImage={handleExportImage}
        onShowSuccess={showSuccess}
      />

      <StatusBar>
        <StatusItem>
          Mode: {mode.charAt(0).toUpperCase() + mode.slice(1)}
        </StatusItem>
        <StatusItem>
          Elements: {mapData.schemaElements?.length || 0}
        </StatusItem>
        <StatusItem>
          Nodes: {nodes.length}
        </StatusItem>
        {saving && (
          <StatusItem $type="warning">
            Saving...
          </StatusItem>
        )}
        {lastSaved && !saving && (
          <StatusItem $type="success">
            Last saved: {lastSaved.toLocaleTimeString()}
          </StatusItem>
        )}
        {hasUnsavedChanges && !saving && (
          <StatusItem $type="warning">
            Unsaved changes
          </StatusItem>
        )}
      </StatusBar>

      <ConfirmModal {...modalProps} />
    </PageContainer>
  );
};

export default MapEditorPage;
